import { Dropable } from '../../../dropdown';

/**
 * Builds the mobile "Sort by" dropdown from the sortable columns and
 * wires it up using the same Dropable/ui-dropdown pattern as the
 * actions column (minus the cancel item). Selecting an option invokes
 * onSelect(column); Dropable closes the menu on item click.
 *
 * Safe to call when there's no mobile sort button or no sortable
 * columns – it simply does nothing and returns null.
 *
 * @param {HTMLElement|null} btn - the mobile sort trigger button
 * @param {Array} columns - the full columns config array (only sortable ones are used)
 * @param {(column: object) => void} onSelect - called when a sort option is chosen
 * @returns {{ wrapper: HTMLElement, menu: HTMLElement }|null}
 */
export function initMobileSortDropdown(btn, columns, onSelect) {
  const sortableColumns = columns.filter((col) => col.sortable);

  if (!btn || !sortableColumns.length) return null;

  const wrapper = document.createElement('div');
  wrapper.className = 'ui-dropdown';
  wrapper.dataset.dropable = '';
  wrapper.dataset.portal = '';

  btn.replaceWith(wrapper);
  wrapper.appendChild(btn);

  btn.setAttribute('aria-haspopup', 'true');
  btn.setAttribute('aria-expanded', 'false');

  const menu = document.createElement('ul');
  menu.className = 'ui-dropdown-menu';

  sortableColumns.forEach((col) => {
    const li = document.createElement('li');
    li.className = 'ui-dropdown-menu__item';
    li.dataset.sortKey = col.key;

    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = col.label || col.key;
    button.addEventListener('click', () => onSelect(col));

    li.appendChild(button);
    menu.appendChild(li);
  });

  wrapper.appendChild(menu);

  new Dropable(wrapper);

  return { wrapper, menu };
}

/**
 * Finds the sortable <th> matching a given column key.
 * @param {HTMLElement[]} sortableHeaders
 * @param {string} key
 * @returns {HTMLElement|undefined}
 */
export function findHeaderByColumnKey(sortableHeaders, key) {
  return sortableHeaders.find((header) => header.dataset.sort === key);
}

/**
 * Computes the next sort state (column key + direction) for a clicked
 * sortable header, toggling ascending/descending. Returns null when the
 * header has no matching column (e.g. stale DOM reference).
 *
 * @param {HTMLElement} th - the clicked/selected sortable header
 * @param {HTMLElement[]} sortableHeaders - all sortable headers in DOM order
 * @param {Array} columns - the full columns config array
 * @returns {{ key: string, direction: 'ascending'|'descending' }|null}
 */
export function getNextSortState(th, sortableHeaders, columns) {
  const thIndex = [...sortableHeaders].indexOf(th);
  const column = columns.filter((col) => col.sortable)[thIndex];
  if (!column) return null;

  const current = th.getAttribute('aria-sort');
  const direction = current === 'ascending' ? 'descending' : 'ascending';

  return { key: column.key, direction };
}

/**
 * Updates aria-sort on every desktop sortable header so only the active
 * column reflects the current direction; all others are reset to 'none'.
 * @param {HTMLElement[]} sortableHeaders
 * @param {string|null} activeColumn
 * @param {string|null} direction
 */
export function updateHeaderSortState(sortableHeaders, activeColumn, direction) {
  sortableHeaders.forEach((header) => {
    header.setAttribute('aria-sort', header.dataset.sort === activeColumn ? direction : 'none');
  });
}

/**
 * Highlights the currently active sort column inside the mobile
 * "Sort by" dropdown, so the selected item reflects the active column
 * regardless of whether sorting was triggered from the dropdown or
 * from a desktop header click.
 * @param {HTMLElement|null} menu - the mobile sort dropdown <ul>
 * @param {string|null} activeColumn
 */
export function updateMobileSortSelection(menu, activeColumn) {
  if (!menu) return;

  menu.querySelectorAll('.ui-dropdown-menu__item').forEach((li) => {
    li.classList.toggle('ui-dropdown-menu__item--selected', li.dataset.sortKey === activeColumn);
  });
}
